import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, tap, distinctUntilChanged, shareReplay } from 'rxjs';

import { UserModel } from '../../../models/user.model';
import { endpoints } from '../../../../environments/endpoints';
import { DefaultResponse } from '../../../interfaces/responses';
import { ApiService } from '../../shared/services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { TokenStorageService } from '../../auth/services/token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly _userSubject = new BehaviorSubject<UserModel | null>(null);
  private readonly _apiService = inject(ApiService);
  private readonly _tokenStorage = inject(TokenStorageService);
  private readonly _translate = inject(TranslateService);

  readonly user$ = this._userSubject.asObservable().pipe(
    distinctUntilChanged((a, b) => a?.id === b?.id)
  );
  
  readonly isLoggedIn$ = this.user$.pipe(
    map(user => user !== null),
    distinctUntilChanged()
  );

  fetchUser(): Observable<UserModel> {
    return this._apiService.get<{ data: UserModel }>(endpoints.user).pipe(
      map(response => response?.data),
      tap(user => {
        if (user) {
          this._setCurrentUser(user);
          this._updateLanguage(user.lang);
        }
      }),
      shareReplay(1)
    );
  }

  logout(): Observable<DefaultResponse<boolean>> {
    return this._apiService.get<DefaultResponse<boolean>>(endpoints.logout).pipe(
      tap(() => this._clearUserSession())
    );
  }

  getCurrentUser(): UserModel | null {
    return this._userSubject.value;
  }

  getUser(): BehaviorSubject<UserModel | null> {
    return this._userSubject;
  }

  isLoggedIn(): boolean {
    const hasUser = this._userSubject.value !== null;
    const hasValidToken = this._tokenStorage.getAccessToken() !== null && 
                         !this._tokenStorage.isAccessTokenExpired();
    return hasUser && hasValidToken;
  }

  updateUser(updatedUser: Partial<UserModel>): void {
    const currentUser = this._userSubject.value;
    if (currentUser) {
      const newUser = { ...currentUser, ...updatedUser };
      this._setCurrentUser(newUser);
    }
  }

  private _setCurrentUser(user: UserModel): void {
    this._userSubject.next(user);
  }

  private _clearUserSession(): void {
    this._userSubject.next(null);
    this._tokenStorage.clearToken();
  }

  private _updateLanguage(language: string): void {
    if (language && this._translate.currentLang !== language) {
      this._translate.use(language);
    }
  }
}
